import React, { useState } from 'react';
import { Box, Typography, Button, Checkbox, FormControlLabel, Paper, Tabs, Tab } from '@mui/material';
import { getAuth } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { CURRENT_LEGAL_VERSION, LEGAL_DOCUMENTS } from '../constants/legalDocuments';
import { updateFinancierLegalConsent, updateCustomerLegalConsent } from '../firebase/dataHandler';

export default function ConsentPage({ role }) {
  const [tab, setTab] = useState(0);
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const auth = getAuth();
  
  const handleAccept = async () => {
    if (!accepted) return;
    setLoading(true);
    try {
      if (role === 'financier') {
        await updateFinancierLegalConsent(CURRENT_LEGAL_VERSION);
      } else if (role === 'customer') {
        const phone = auth.currentUser.phoneNumber;
        await updateCustomerLegalConsent(phone, CURRENT_LEGAL_VERSION);
      }
      // Force reload to bypass RequireConsent wrapper
      window.location.reload();
    } catch (e) {
      console.error(e);
      alert('Failed to record consent. Please try again.');
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await auth.signOut();
    navigate('/');
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'var(--bg-base)',
      padding: { xs: 2, md: 4 }
    }}>
      <Paper sx={{
        maxWidth: 800,
        width: '100%',
        backgroundColor: 'var(--bg-surface)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 4,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '90vh'
      }}>
        <Box sx={{ p: 4, borderBottom: '1px solid var(--border-subtle)', textAlign: 'center' }}>
          <Typography variant="h5" sx={{ color: 'var(--text-primary)', fontWeight: 800, fontFamily: 'Inter' }}>
            Updates to our Terms & Policies
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mt: 1, fontFamily: 'Inter' }}>
            We've updated our legal agreements. Please read and accept them to continue using Finaculator.
          </Typography>
        </Box>

        <Tabs 
          value={tab} 
          onChange={(e, v) => setTab(v)} 
          variant="fullWidth"
          sx={{ borderBottom: '1px solid var(--border-subtle)' }}
        >
          <Tab label="Terms of Service" sx={{ color: 'var(--text-primary)', fontWeight: 600, textTransform: 'none' }} />
          <Tab label="Privacy Policy" sx={{ color: 'var(--text-primary)', fontWeight: 600, textTransform: 'none' }} />
          <Tab label="Data Policy" sx={{ color: 'var(--text-primary)', fontWeight: 600, textTransform: 'none' }} />
        </Tabs>

        <Box sx={{ flex: 1, overflowY: 'auto', p: 4, backgroundColor: 'var(--bg-base)' }}>
          <Box sx={{ 
            color: 'var(--text-secondary)', 
            fontFamily: 'Inter',
            whiteSpace: 'pre-wrap',
            '& h1': { fontSize: '1.5rem', color: 'var(--text-primary)', mb: 2 },
            '& h2': { fontSize: '1.2rem', color: 'var(--accent-teal)', mt: 3, mb: 1 },
            '& p': { lineHeight: 1.6, mb: 2 },
            '& ul': { mb: 2, paddingLeft: 3 }
          }}>
            {tab === 0 && <div dangerouslySetInnerHTML={{ __html: LEGAL_DOCUMENTS.terms.content.replace(/\n\n/g, '<br/><br/>').replace(/^# (.*$)/gim, '<h1>$1</h1>').replace(/^## (.*$)/gim, '<h2>$1</h2>').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />}
            {tab === 1 && <div dangerouslySetInnerHTML={{ __html: LEGAL_DOCUMENTS.privacy.content.replace(/\n\n/g, '<br/><br/>').replace(/^# (.*$)/gim, '<h1>$1</h1>').replace(/^## (.*$)/gim, '<h2>$1</h2>').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />}
            {tab === 2 && <div dangerouslySetInnerHTML={{ __html: LEGAL_DOCUMENTS.dataPolicy.content.replace(/\n\n/g, '<br/><br/>').replace(/^# (.*$)/gim, '<h1>$1</h1>').replace(/^## (.*$)/gim, '<h2>$1</h2>').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />}
          </Box>
        </Box>

        <Box sx={{ p: 3, borderTop: '1px solid var(--border-subtle)', backgroundColor: 'var(--bg-surface)' }}>
          <FormControlLabel
            control={<Checkbox checked={accepted} onChange={(e) => setAccepted(e.target.checked)} sx={{ color: 'var(--accent-blue)', '&.Mui-checked': { color: 'var(--accent-teal)' } }} />}
            label={<Typography sx={{ color: 'var(--text-primary)', fontSize: 14 }}>I have read and agree to the Terms of Service, Privacy Policy, and Data Handling Policy.</Typography>}
            sx={{ mb: 3 }}
          />
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={handleSignOut} sx={{ color: 'var(--text-secondary)', borderColor: 'var(--border-subtle)', textTransform: 'none', fontWeight: 600 }}>
              Cancel & Sign Out
            </Button>
            <Button 
              variant="contained" 
              onClick={handleAccept} 
              disabled={!accepted || loading}
              sx={{ background: 'var(--brand-primary)', color: 'var(--text-primary)', textTransform: 'none', fontWeight: 600 }}
            >
              Accept & Continue
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
